import { Redirect } from "react-router-dom";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
// import axios
import axios from "axios";
import userStore from "../Stores/User";
import { observer } from "mobx-react";
// Import loading gif
import loadingIcon from "../Assets/loading.gif";
import mixpanel from "mixpanel-browser";
import { useStripe as useStripeContext } from "../StripeProvider";
import { validateCoupon } from "../audiblogsApi";

const STRIPE_API_KEY = process.env.REACT_APP_STRIPE_API_KEY;
const stripePromise = loadStripe(STRIPE_API_KEY);

const appearance = {
  theme: "night",
  variables: {
    colorPrimary: "#fff",
    colorBackground: "rgba(24,15,34,.85)",
    colorText: "#fff",
    colorDanger: "#df1b41",
    fontFamily: "Ideal Sans, system-ui, sans-serif",
    spacingUnit: "2px",
    borderRadius: "4px",
  },
  rules: {
    ".Input": {
      color: "#fff",
    },
    ".Terms": {
      display: "none",
    },
  },
  labels: "floating",
};

// Helper function to format coupon message
const formatCouponMessage = (couponDetails) => {
  let message = "";

  // Format the discount amount
  if (couponDetails.percent_off) {
    message = `${couponDetails.percent_off}% off`;
  } else if (couponDetails.amount_off) {
    const amount = (couponDetails.amount_off / 100).toFixed(2);
    const currency = couponDetails.currency.toUpperCase();
    message = `${currency} ${amount} off`;
  }

  // Add duration information
  if (
    couponDetails.duration === "repeating" &&
    couponDetails.duration_in_months
  ) {
    message += ` for ${couponDetails.duration_in_months} months`;
  } else if (couponDetails.duration === "once") {
    message += ` (one-time)`;
  } else if (couponDetails.duration === "forever") {
    message += ` forever`;
  }

  // Add minimum amount restriction if present
  if (couponDetails.restrictions?.minimum_amount) {
    const minAmount = (couponDetails.restrictions.minimum_amount / 100).toFixed(
      2
    );
    const currency = couponDetails.restrictions.minimum_amount_currency.toUpperCase();
    message += ` (min. ${currency} ${minAmount})`;
  }

  return message || "Coupon applied successfully!";
};

// Initial form without Stripe hooks
const InitialCheckoutForm = observer(
  ({
    onCouponSubmit,
    couponError,
    couponSuccess,
    onAddPaymentDetails,
    isLoading,
  }) => {
    const [couponCode, setCouponCode] = useState("");

    const handleCouponChange = (event) => {
      setCouponCode(event.target.value);
    };

    const handleCouponApply = () => {
      if (!couponCode.trim()) return;
      onCouponSubmit(couponCode.trim());
    };

    return (
      <div
        style={{
          maxWidth: "400px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            marginTop: "20px",
            marginBottom: "10px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <input
            type="text"
            value={couponCode}
            onChange={handleCouponChange}
            placeholder="Coupon code"
            style={{
              flex: 1,
              padding: "8px 12px",
              backgroundColor: "rgba(24,15,34,1)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          />
          <button
            type="button"
            onClick={handleCouponApply}
            disabled={isLoading}
            style={{
              padding: "8px 12px",
              backgroundColor: "transparent",
              color: "#fff",
              border: "1px solid #fff",
              borderRadius: "4px",
              cursor: isLoading ? "not-allowed" : "pointer",
              fontSize: "14px",
              opacity: isLoading ? 0.7 : 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            Apply
            {isLoading && (
              <img
                src={loadingIcon}
                alt="Loading..."
                style={{ width: "16px", height: "16px" }}
              />
            )}
          </button>
        </div>

        {couponError && (
          <div
            style={{
              color: "#df1b41",
              fontSize: "14px",
              marginBottom: "15px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <span style={{ fontWeight: "bold" }}>✕</span> {couponError}
          </div>
        )}

        {couponSuccess && (
          <div
            style={{
              color: "#4ade80",
              fontSize: "14px",
              marginBottom: "15px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <span style={{ fontWeight: "bold" }}>✓</span> {couponSuccess}
          </div>
        )}

        <button
          type="button"
          onClick={onAddPaymentDetails}
          style={{
            width: "100%",
            minHeight: "44px",
            background: "linear-gradient(90deg, #4F46E5 0%, #E11D48 100%)",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "16px",
            padding: "10px 20px",
            marginTop: "15px",
            marginBottom: "15px",
          }}
        >
          Add Payment Details
        </button>
      </div>
    );
  }
);

// Payment form with Stripe hooks
const PaymentCheckoutForm = observer(
  ({ onCouponSubmit, couponError, couponSuccess, onBack }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [message, setMessage] = useState("");

    useEffect(() => {
      // Wait for the payment element to be rendered
      const timer = setTimeout(() => {
        const paymentElement = document.querySelector(".StripeElement");
        if (paymentElement) {
          paymentElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }, []);

    const handleSubmit = async (event) => {
      event.preventDefault();

      // Early return if already processing or missing dependencies
      if (isProcessing || !stripe || !elements) {
        return;
      }

      setIsProcessing(true);

      try {
        const { error } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/onboarding/quickstart/`,
          },
        });

        if (error) {
          setMessage(error.message);
        }
      } catch (e) {
        setMessage("An unexpected error occurred.");
      } finally {
        setIsProcessing(false);
      }
    };

    return (
      <form
        onSubmit={handleSubmit}
        style={{
          maxWidth: "400px",
          margin: "0 auto",
        }}
      >
        {/* Coupon Feedback Messages */}
        {couponError && (
          <div
            style={{
              color: "#df1b41",
              fontSize: "14px",
              marginBottom: "15px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <span style={{ fontWeight: "bold" }}>✕</span> {couponError}
          </div>
        )}

        {couponSuccess && (
          <div
            style={{
              color: "#4ade80",
              fontSize: "14px",
              marginBottom: "15px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <span style={{ fontWeight: "bold" }}>✓</span> {couponSuccess}
          </div>
        )}

        <PaymentElement />

        <div
          style={{
            marginTop: "15px",
          }}
        >
          <button
            disabled={isProcessing || !stripe || !elements}
            className="cta-button"
            type="submit"
            style={{
              width: "100%",
              minHeight: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(90deg, #4F46E5 0%, #E11D48 100%)",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
              padding: "10px 20px",
            }}
          >
            {isProcessing ? (
              <img
                src={loadingIcon}
                alt="Loading..."
                style={{ width: "20px", height: "20px" }}
              />
            ) : (
              "Start trial for $0"
            )}
          </button>
        </div>
        {message && (
          <div
            style={{
              color: "#df1b41",
              textAlign: "center",
              marginTop: "10px",
              marginBottom: "10px",
              fontSize: "14px",
            }}
          >
            {message}
          </div>
        )}
      </form>
    );
  }
);

const StripeCheckout = observer(
  ({ planType, onSetupIntentCreated, onShowPaymentElement }) => {
    const [clientSecret, setClientSecret] = useState("");
    const [couponCode, setCouponCode] = useState("");
    const [couponError, setCouponError] = useState("");
    const [couponSuccess, setCouponSuccess] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isCouponLoading, setIsCouponLoading] = useState(false);
    const [error, setError] = useState(null);
    const [couponDetails, setCouponDetails] = useState(null);
    const [showPaymentElement, setShowPaymentElement] = useState(false);
    const [previousSetupIntentId, setPreviousSetupIntentId] = useState(() =>
      localStorage.getItem("lastSetupIntentId")
    );

    const stripe = useStripeContext();

    // Notify parent when showPaymentElement changes
    useEffect(() => {
      onShowPaymentElement?.(showPaymentElement);
    }, [showPaymentElement, onShowPaymentElement]);

    const createSetupIntent = async () => {
      try {
        setIsLoading(true);
        setError(null);

        let token = "";
        await new Promise((resolve) => {
          window.grecaptcha.enterprise.ready(async () => {
            token = await window.grecaptcha.enterprise.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {action: 'LOGIN'});
            resolve();
          });
        });

        console.log("recaptcha token", token);
        
        const headers = { "Audiblogs-Id-Token": userStore.idToken };

        const data = {
          metadata: {
            utm_source: mixpanel.get_property("utm_source"),
            utm_medium: mixpanel.get_property("utm_medium"),
            utm_campaign: mixpanel.get_property("utm_campaign"),
            utm_term: mixpanel.get_property("utm_term"),
            search_engine: mixpanel.get_property("$search_engine"),
            search_keyword: mixpanel.get_property("mp_keyword"),
            referrer: mixpanel.get_property("$referrer"),
            initial_referrer: mixpanel.get_property("$initial_referrer"),
            tolt_referral: window.tolt_referral,
            subscription_type: planType,
            coupon: couponCode || "",
            recaptcha_token: token,
          },
          setup_intent_id: previousSetupIntentId,
        };

        const result = await axios.post(
          process.env.REACT_APP_BACKEND_URL +
            "/api/payments/create_setup_intent",
          data,
          { headers }
        );

        if (result.data.error) {
          setError(result.data.error);
        } else if (result.data && result.data.client_secret) {
          const newSetupIntentId = result.data.setup_intent_id;
          setPreviousSetupIntentId(newSetupIntentId);
          localStorage.setItem("lastSetupIntentId", newSetupIntentId);
          setClientSecret(result.data.client_secret);
          setShowPaymentElement(true);
          onSetupIntentCreated?.(newSetupIntentId);
        } else {
          setError("Invalid response from server");
        }
      } catch (error) {
        console.error("Error creating setup intent:", error);
        const errorMessage =
          "We detected some unusual behavior. Please try again and contact support if the issue persists.";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    const handleCouponSubmit = async (code) => {
      if (!code.trim()) return;

      try {
        setIsCouponLoading(true);
        setCouponError("");
        setCouponSuccess("");
        setCouponDetails(null);

        const result = await validateCoupon(userStore.idToken, code.trim());

        if (result.error) {
          setCouponError(result.error);
          setCouponCode("");
        } else if (result.valid && result.coupon_details) {
          setCouponDetails(result.coupon_details);
          setCouponSuccess(formatCouponMessage(result.coupon_details));
          setCouponCode(code.trim());
        } else {
          setCouponError("Invalid coupon code");
          setCouponCode("");
        }
      } catch (error) {
        console.error("Error handling coupon:", error);
        setCouponError("Failed to validate coupon");
        setCouponCode("");
      } finally {
        setIsCouponLoading(false);
      }
    };

    const handleAddPaymentDetails = () => {
      createSetupIntent();

      // Add a second check after more time
      setTimeout(() => {
        const paymentElement = document.querySelector(".StripeElement");
        if (paymentElement) {
          window.scrollTo({
            top: paymentElement.offsetTop - 50,
            behavior: "smooth",
          });
        }
      }, 2000);
    };

    const handleBack = () => {
      setShowPaymentElement(false);
    };

    // Check if user has an active subscription
    const hasActiveSubscription =
      userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id;

    // Clear setup intent ID from localStorage when payment is successful
    useEffect(() => {
      if (userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id) {
        localStorage.removeItem("lastSetupIntentId");
      }
    }, [userStore.user?.subscriptions?.raw_subscriptions]);

    if (hasActiveSubscription) {
      return <Redirect to={userStore.customerPortalUrl} />;
    }

    if (!stripe || isLoading) {
      return (
        <div style={{ textAlign: "center" }}>
          <img src={loadingIcon} width={"50"} alt="Loading..." />
        </div>
      );
    }

    return (
      <div style={{ paddingBottom: "40px" }}>
        {error ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ 
              color: "#df1b41", 
              marginBottom: "20px",
              fontSize: "14px",
              padding: "0 20px"
            }}>
              {error}
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "10px 20px",
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              Try Again
            </button>
          </div>
        ) : clientSecret && showPaymentElement ? (
          <Elements
            stripe={stripePromise}
            options={{ appearance, clientSecret }}
          >
            <PaymentCheckoutForm
              onCouponSubmit={handleCouponSubmit}
              couponError={couponError}
              couponSuccess={couponSuccess}
              onBack={handleBack}
            />
          </Elements>
        ) : (
          <InitialCheckoutForm
            onCouponSubmit={handleCouponSubmit}
            couponError={couponError}
            couponSuccess={couponSuccess}
            onAddPaymentDetails={handleAddPaymentDetails}
            isLoading={isCouponLoading}
          />
        )}
        <div
          style={{
            textAlign: "center",
            marginTop: "30px",
            marginBottom: "20px",
            fontSize: "12px",
            color: "#666",
            padding: "0 20px",
          }}
        >
          By continuing, you agree to our{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#fff", textDecoration: "underline" }}
          >
            Terms of Service
          </a>
        </div>
      </div>
    );
  }
);

// Export
export default StripeCheckout;
